import { useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import styles from "./GridV2PricingComparisonTable.module.scss";
import { TenantIconNameEnum } from "@/types/tenantIcons";

export interface GridV2PricingComparisonTableProps {
  className?: string;
}

/**
 * Grid V2 A/B test: the original vs new fixed pricing comparison, embedded in
 * the early access welcome and in the "Here's how new fixed pricing is
 * different" modal. Three columns on desktop; below `md` the label column is
 * folded into each cell (see the SCSS) while the row headers keep the
 * accessible structure.
 */
export const GridV2PricingComparisonTable = ({
  className,
}: GridV2PricingComparisonTableProps) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "GridV2PricingComparisonTable");

  return (
    // Never scrolls sideways: the columns are percentages of whatever width
    // the host gives and the copy wraps.
    <div className={clsx(block, className)}>
      <div className={cn("frame")}>
        {/* Decorative yellow frame around the New pricing column. */}
        <div className={cn("highlight")} aria-hidden="true" />
        <table className={cn("table")}>
          <caption className={cn("caption")}>
            {t("gridV2.pricingComparisonTable.tableLabel")}
          </caption>
          <colgroup>
            <col className={cn("col", ["label"])} />
            <col className={cn("col", ["original"])} />
            <col className={cn("col", ["new"])} />
          </colgroup>
          <thead>
            <tr>
              <td />
              <th scope="col" className={cn("column-header")}>
                {t("gridV2.pricingComparisonTable.originalPricing")}
              </th>
              <th scope="col" className={cn("column-header", ["new"])}>
                <span className={cn("refreshed-tag")}>
                  {t("gridV2.pricingComparisonTable.refreshed")}
                </span>
                {t("gridV2.pricingComparisonTable.newPricing")}
              </th>
            </tr>
          </thead>
          <tbody>
            <ComparisonRow
              cn={cn}
              label={t("gridV2.pricingComparisonTable.rows.clear.label")}
              original={t("gridV2.pricingComparisonTable.rows.clear.original")}
              next={t("gridV2.pricingComparisonTable.rows.clear.new")}
            />
            <ComparisonRow
              cn={cn}
              label={t("gridV2.pricingComparisonTable.rows.inclusive.label")}
              original={t(
                "gridV2.pricingComparisonTable.rows.inclusive.original"
              )}
              next={t("gridV2.pricingComparisonTable.rows.inclusive.new")}
            />
            <ComparisonRow
              cn={cn}
              label={t("gridV2.pricingComparisonTable.rows.flexible.label")}
              original={t(
                "gridV2.pricingComparisonTable.rows.flexible.original"
              )}
              next={t("gridV2.pricingComparisonTable.rows.flexible.new")}
            />
            <ComparisonRow
              cn={cn}
              label={t("gridV2.pricingComparisonTable.rows.example.label")}
              dates={t("gridV2.pricingComparisonTable.rows.example.dates")}
              original={
                <ExamplePrice
                  cn={cn}
                  price={t(
                    "gridV2.pricingComparisonTable.rows.example.originalPrice"
                  )}
                  note={t(
                    "gridV2.pricingComparisonTable.rows.example.originalPriceNote"
                  )}
                />
              }
              next={
                <ExamplePrice
                  cn={cn}
                  price={t(
                    "gridV2.pricingComparisonTable.rows.example.newPrice"
                  )}
                  note={t(
                    "gridV2.pricingComparisonTable.rows.example.newPriceNote"
                  )}
                />
              }
            />
          </tbody>
        </table>
      </div>
    </div>
  );
};

type ElementClassName = ReturnType<typeof useModuleBEM>[1];

const ComparisonRow = ({
  cn,
  label,
  dates,
  original,
  next,
}: {
  cn: ElementClassName;
  label: string;
  /** Example row only: shown under the label. */
  dates?: string;
  original: React.ReactNode;
  next: React.ReactNode;
}) => {
  // Below `md` the label column is hidden and each cell shows this copy of the
  // label; it is decorative, the row header stays the accessible name.
  const cellLabel = (
    <span className={cn("cell-label")} aria-hidden="true">
      {label}
      {dates != null && <span className={cn("dates")}>{dates}</span>}
    </span>
  );
  return (
    <tr className={cn("row")}>
      <th scope="row" className={cn("row-label")}>
        {label}
        {dates != null && <span className={cn("dates")}>{dates}</span>}
      </th>
      <td className={cn("cell")}>
        {cellLabel}
        {original}
      </td>
      <td className={cn("cell", ["new"])}>
        {cellLabel}
        {next}
      </td>
    </tr>
  );
};

const ExamplePrice = ({
  cn,
  price,
  note,
}: {
  cn: ElementClassName;
  price: string;
  note: string;
}) => {
  const { t } = useI18nContext();
  return (
    <span className={cn("example")}>
      <span className={cn("example-route")}>
        {/* Screen readers read "< >" as "less than greater than"; the spoken
            copy replaces it. */}
        <span aria-hidden="true">
          {t("gridV2.pricingComparisonTable.rows.example.route")}
        </span>
        <span className={cn("example-route-spoken")}>
          {t("gridV2.pricingComparisonTable.rows.example.routeSpoken")}
        </span>
        <Icon iconName={TenantIconNameEnum.mapPinFill} aria-hidden />
      </span>
      <span className={cn("example-price")}>{price}</span>
      <span className={cn("example-note")}>{note}</span>
    </span>
  );
};
